import { useState, useCallback, forwardRef, type ChangeEvent, type FocusEvent } from 'react';

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  name?: string;
  className?: string;
}

// Format: +998 XX XXX XX XX
const PREFIX = '+998';
const MASK_TEMPLATE = '+998 __ ___ __ __';

function formatPhoneNumber(digits: string): string {
  // Only keep digits after +998
  const clean = digits.replace(/\D/g, '').slice(0, 9);

  if (clean.length === 0) return PREFIX + ' ';

  let formatted = PREFIX + ' ';

  // XX
  formatted += clean.slice(0, 2);
  if (clean.length > 2) {
    formatted += ' ' + clean.slice(2, 5);
  }
  if (clean.length > 5) {
    formatted += ' ' + clean.slice(5, 7);
  }
  if (clean.length > 7) {
    formatted += ' ' + clean.slice(7, 9);
  }

  return formatted;
}

function extractDigits(formatted: string): string {
  // Remove +998 and all non-digits
  return formatted.replace(/\D/g, '').slice(3);
}

function getRawPhoneNumber(digits: string): string {
  // Return full phone number with country code for storage
  return '+998' + digits;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value = '', onChange, onBlur, name, className }, ref) => {
    const [displayValue, setDisplayValue] = useState(() => {
      // Initialize from existing value
      if (value && value.startsWith('+998')) {
        const digits = value.replace(/\D/g, '').slice(3);
        return formatPhoneNumber(digits);
      }
      return PREFIX + ' ';
    });

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      // Don't allow removing the prefix
      if (!input.startsWith(PREFIX)) {
        return;
      }

      // Extract just the digits after prefix
      const digits = extractDigits(input);

      // Limit to 9 digits
      if (digits.length > 9) {
        return;
      }

      const formatted = formatPhoneNumber(digits);
      setDisplayValue(formatted);

      // Send raw phone number to form
      if (onChange) {
        onChange(getRawPhoneNumber(digits));
      }
    }, [onChange]);

    const handleFocus = useCallback(() => {
      // Position cursor at the end when focused
      if (displayValue === PREFIX + ' ') {
        setDisplayValue(PREFIX + ' ');
      }
    }, [displayValue]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const cursorPos = target.selectionStart || 0;

      // Prevent deleting the prefix
      if (e.key === 'Backspace' && cursorPos <= 5) {
        e.preventDefault();
      }

      // Prevent arrow keys from going into prefix
      if (e.key === 'ArrowLeft' && cursorPos <= 5) {
        e.preventDefault();
      }
    }, []);

    return (
      <input
        ref={ref}
        type="tel"
        name={name}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        placeholder={MASK_TEMPLATE}
        className={className}
        inputMode="numeric"
        autoComplete="tel"
      />
    );
  }
);

PhoneInput.displayName = 'PhoneInput';
